import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import {
  FileSpreadsheet, HelpCircle, NotebookText, FolderKanban, Archive, Users,
  UserCog, CheckCircle2, Coins, Rocket, Cloud, Lock,
  Phone, Target, Settings,
  ClipboardList, RefreshCw, FileText, BarChart3, UserCircle, PenLine,
  ShieldCheck, Landmark, Unlock,
  HandHeart, GraduationCap,
  Mail, MessageCircle, MapPin, Calendar, Check, Star, ExternalLink,
} from 'lucide-react';
import './App.css';

const EMAILJS_SERVICE_ID = 'service_6tlx1bo';
const EMAILJS_TEMPLATE_ID = 'template_zlkd5j8';
const EMAILJS_PUBLIC_KEY = 'uFeefHzT0iNQYQJTf';
const WHATSAPP_NUMBER = '233553208451';

function useInView(threshold = 0.15) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={`reveal ${visible ? 'revealed' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function WhatsAppButton() {
  return (
    <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hello%20CareTrack%20Ghana%2C%20I%20would%20like%20to%20request%20a%20free%20demo.`} target="_blank" rel="noopener noreferrer" className="whatsapp-btn" aria-label="Chat on WhatsApp">
      <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="whatsapp-tooltip">Chat with us</span>
    </a>
  );
}

function Navbar({ active, setActive }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const links = ['Home', 'Features', 'About', 'Pricing', 'Contact'];
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const handleNav = (page) => { setActive(page); setMenuOpen(false); };
  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-inner">
        <div className="nav-brand" onClick={() => handleNav('Home')}>
          <img src="/logo.png" alt="CareTrack Ghana Logo" className="nav-logo-img" />
        </div>
        <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
          {links.map(l => (
            <button key={l} className={`nav-link ${active === l ? 'active' : ''}`} onClick={() => handleNav(l)}>{l}</button>
          ))}
          <button className="nav-cta" onClick={() => handleNav('Contact')}>Request a Free Demo →</button>
        </div>
        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </div>
    </nav>
  );
}

function GhanaMap() {
  return (
    <div className="ghana-map-bg" aria-hidden="true">
      <svg viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg" className="ghana-svg">
        <defs>
          <linearGradient id="mapGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#27AE60" stopOpacity="0.15"/>
            <stop offset="100%" stopColor="#F39C12" stopOpacity="0.05"/>
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <path d="M150 20 C120 22, 90 35, 72 55 C55 75, 48 100, 45 125 C42 150, 44 170, 40 192 C36 214, 28 230, 30 252 C32 274, 48 290, 65 308 C82 326, 105 342, 130 355 C145 362, 152 368, 162 365 C175 360, 190 348, 205 335 C222 320, 238 302, 248 282 C258 262, 260 240, 258 218 C256 196, 248 178, 245 158 C242 138, 244 118, 238 98 C232 78, 218 60, 200 45 C185 32, 168 18, 150 20Z"
          fill="url(#mapGrad)" stroke="#27AE60" strokeWidth="1.5" strokeOpacity="0.3" filter="url(#glow)" className="map-path"/>
        {[
          { cx: 150, cy: 120, r: 4, delay: 0, color: '#F39C12' },
          { cx: 90, cy: 160, r: 3, delay: 0.5, color: '#27AE60' },
          { cx: 210, cy: 155, r: 3, delay: 1, color: '#2980B9' },
          { cx: 75, cy: 220, r: 4, delay: 1.5, color: '#F39C12' },
          { cx: 225, cy: 215, r: 3, delay: 0.8, color: '#27AE60' },
          { cx: 100, cy: 280, r: 3, delay: 2, color: '#8E44AD' },
          { cx: 200, cy: 275, r: 3, delay: 2.5, color: '#E67E22' },
          { cx: 150, cy: 310, r: 4, delay: 0.3, color: '#27AE60' },
          { cx: 130, cy: 190, r: 3, delay: 1.8, color: '#2980B9' },
          { cx: 170, cy: 245, r: 3, delay: 1.2, color: '#F39C12' },
        ].map((dot, i) => (
          <circle key={i} cx={dot.cx} cy={dot.cy} r={dot.r} fill={dot.color} opacity="0.7" className="map-dot" style={{ animationDelay: `${dot.delay}s` }} />
        ))}
        <line x1="150" y1="120" x2="90" y2="160" stroke="#27AE60" strokeWidth="0.8" strokeOpacity="0.25" strokeDasharray="4 4" className="map-line"/>
        <line x1="150" y1="120" x2="210" y2="155" stroke="#F39C12" strokeWidth="0.8" strokeOpacity="0.25" strokeDasharray="4 4" className="map-line"/>
        <line x1="90" y1="160" x2="75" y2="220" stroke="#27AE60" strokeWidth="0.8" strokeOpacity="0.25" strokeDasharray="4 4" className="map-line"/>
        <line x1="210" y1="155" x2="225" y2="215" stroke="#2980B9" strokeWidth="0.8" strokeOpacity="0.25" strokeDasharray="4 4" className="map-line"/>
        <line x1="75" y1="220" x2="100" y2="280" stroke="#F39C12" strokeWidth="0.8" strokeOpacity="0.25" strokeDasharray="4 4" className="map-line"/>
        <line x1="225" y1="215" x2="200" y2="275" stroke="#27AE60" strokeWidth="0.8" strokeOpacity="0.25" strokeDasharray="4 4" className="map-line"/>
        <line x1="100" y1="280" x2="150" y2="310" stroke="#8E44AD" strokeWidth="0.8" strokeOpacity="0.25" strokeDasharray="4 4" className="map-line"/>
        <line x1="200" y1="275" x2="150" y2="310" stroke="#E67E22" strokeWidth="0.8" strokeOpacity="0.25" strokeDasharray="4 4" className="map-line"/>
        <line x1="130" y1="190" x2="90" y2="160" stroke="#2980B9" strokeWidth="0.8" strokeOpacity="0.2" strokeDasharray="4 4" className="map-line"/>
        <line x1="130" y1="190" x2="170" y2="245" stroke="#F39C12" strokeWidth="0.8" strokeOpacity="0.2" strokeDasharray="4 4" className="map-line"/>
        <line x1="170" y1="245" x2="200" y2="275" stroke="#27AE60" strokeWidth="0.8" strokeOpacity="0.2" strokeDasharray="4 4" className="map-line"/>
      </svg>
    </div>
  );
}

function Hero({ setActive }) {
  const [ref, visible] = useInView(0.1);
  const ngos = useCountUp(200, 2500, visible);
  const districts = useCountUp(23, 2000, visible);
  const components = useCountUp(7, 1500, visible);
  const taglines = ["Works for NGOs & Foundations", "Works for Schools & Tutors"];
  const [taglineIdx, setTaglineIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTaglineIdx(i => (i + 1) % taglines.length), 3500);
    return () => clearInterval(id);
  }, [taglines.length]);
  return (
    <section className="hero" ref={ref}>
      <GhanaMap />
      <div className="hero-content">
        <div className={`hero-text ${visible ? 'hero-text-visible' : ''}`}>
          <div className="hero-eyebrow">Built for organisations that serve people</div>
          <h1 className="hero-h1">Case Management Software that <span className="hero-accent hero-rotate" key={taglineIdx}>{taglines[taglineIdx]}</span></h1>
          <p className="hero-sub">We build <strong>CASE MANAGEMENT SYSTEMS</strong> for NGOs, foundations, and schools.</p>
          <div className="hero-btns">
            <button className="btn-primary" onClick={() => setActive('Contact')}>Request a Free Demo →</button>
            <button className="btn-outline" onClick={() => setActive('Features')}>See Features</button>
          </div>
        </div>
        <div className={`hero-card ${visible ? 'hero-card-visible' : ''}`}>
          <div className="hero-card-header">
            <div className="hero-card-dot green"/><div className="hero-card-dot gold"/><div className="hero-card-dot red"/>
            <span>CareTrack Dashboard: Live Preview</span>
          </div>
          <div className="hero-card-body">
            <div className="dash-stat"><span className="dash-num">124</span><span className="dash-label">Beneficiaries</span></div>
            <div className="dash-stat"><span className="dash-num">7</span><span className="dash-label">Components</span></div>
            <div className="dash-stat"><span className="dash-num">38</span><span className="dash-label">Follow-ups</span></div>
          </div>
          {[
            { label: 'Youth Dev', pct: 78, color: 'green' },
            { label: 'Child Welfare', pct: 61, color: 'green' },
            { label: 'Skills Training', pct: 45, color: 'gold' },
          ].map((b, i) => (
            <div className="dash-bar-row" key={i}>
              <span className="dash-bar-label">{b.label}</span>
              <div className="dash-bar-track"><div className={`dash-bar-fill ${b.color}-fill`} style={{ width: visible ? `${b.pct}%` : '0%' }} /></div>
              <span className="dash-bar-pct">{b.pct}%</span>
            </div>
          ))}
          <div className="dash-badges">
            <span className="dash-badge green-badge">✓ 12 SIRs generated</span>
            <span className="dash-badge gold-badge">● 5 pending follow-ups</span>
          </div>
        </div>
      </div>
      <div className="hero-stats">
        {[
          { num: ngos, suffix: '+', label: 'Organisations that need this solution' },
          { num: districts, suffix: '', label: 'Districts currently served' },
          { num: components, suffix: '', label: 'Programme components managed' },
          { num: null, label: 'Already operational in Ghana', live: true },
        ].map((s, i) => (
          <React.Fragment key={i}>
            {i > 0 && <div className="stat-divider" />}
            <div className="stat-item">
              {s.live ? <div className="stat-num live-badge">LIVE</div> : <div className="stat-num">{s.num}{s.suffix}</div>}
              <div className="stat-lbl">{s.label}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

function ProblemWeSolve() {
  const problems = [
    { icon: FileSpreadsheet, title: 'Data Trapped in Spreadsheets', body: 'Critical beneficiary information sits in Excel files on one person\'s laptop. Nobody else can access it, update it, or recover it when things go wrong.' },
    { icon: HelpCircle, title: 'Nobody Knows the Full Picture', body: 'Without the right systems in place, even basic questions become difficult for Management to answer. How many active cases are we managing? Which clients have not been followed up recently? Are our interventions making a measurable difference?' },
    { icon: NotebookText, title: 'Cases Lost in Notebooks', body: 'Many organisations still rely on handwritten case notes stored in notebooks and paper files. When records go missing or staff members leave, important client information can disappear with them, making continuity of care a challenge.' },
    { icon: FolderKanban, title: 'Information Stored in Spreadsheets', body: 'Important beneficiary data is frequently kept in separate Excel files managed by individual staff members. This limits collaboration, creates dependency on specific individuals, and increases the risk of data loss or inconsistency.' },
    { icon: Archive, title: 'Files Buried in Cabinets', body: 'Important records are often stored in filing cabinets and storage rooms, making them difficult to access when needed. As files accumulate over time, tracking case histories, preparing reports, and conducting audits becomes increasingly challenging.' },
    { icon: Users, title: 'Knowledge Walks Out the Door', body: 'When a programme officer resigns or transfers, they take everything they know about their cases with them. The organisation starts from scratch with every new staff member, losing continuity and institutional memory.' },
  ];
  return (
    <section className="section problem-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">Sound familiar?</div>
            <h2 className="section-title">The problem we solve</h2>
            <div className="gold-line" />
            <p className="section-desc">Every week, thousands of organisations lose critical information, productive time, and struggle to provide essential services to their clients. This is not because they lack passion but because they lack the right tools. Here is what that looks like on the ground.</p>
          </div>
        </Reveal>
        <div className="problem-grid">
          {problems.map((p, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="problem-card">
                <div className="problem-icon"><p.icon size={26} strokeWidth={1.75} color="#fff" /></div>
                <div className="problem-title">{p.title}</div>
                <div className="problem-body">{p.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function OurStory() {
  return (
    <section className="section story-section">
      <div className="section-inner">
        <div className="story-grid">
          <Reveal className="story-visual">
            <div className="founder-frame-wrap">
              <div className="founder-frame-border">
                <div className="founder-frame-spin" />
              </div>
              <div className="founder-frame-inner">
                <img src="/founder.png" alt="Mark Effah Yeboah, Founder CareTrack Ghana" className="founder-photo" />
              </div>
              <div className="story-badge">CEO — Mark Effah Yeboah</div>
            </div>
          </Reveal>
          <Reveal delay={200} className="story-text">
            <div className="section-eyebrow">Our Story</div>
            <h2 className="section-title">Born from frustration. Built with purpose.</h2>
            <div className="gold-line" style={{ margin: '12px 0 20px' }} />
            <p className="story-para">Over the years, I have watched incredible teams around the world. I have witnessed people running youth programs, women's shelters, legal aid clinics, and district welfare offices pour their hearts into their communities. They have the passion, but behind the scenes, they are completely drowning in administrative chaos.</p>
            <p className="story-para">When you actually sit in these offices, you discover confidential cases scribbled in notebooks that get misplaced, critical case histories trapped in messy spreadsheets, or worse, just sitting in a cupboard around the corner. When clients are waiting on support, or a staff member suddenly resigns, finding files and rendering services becomes a major challenge.</p>
            <p className="story-para">That is exactly why I built <strong>CareTrack Ghana</strong>.</p>
            <p className="story-para">You cannot run a modern organisation on fragmented data. Whether you are an international NGO, a school managing student welfare, or a local group caring for the elderly, you need the full story of the people you support. You need to know where each case started, what help was received, and exactly where each client stands.</p>
            <p className="story-para">CareTrack Ghana centralises Social Inquiry Reports (SIR), referrals, and interventions into one secure, intuitive case management platform. We build the digital foundation that stops the paperwork, to enable Project Offices to work seamlessly and ensure no one falls through the cracks.</p>
            <div className="story-quote">
              <span className="story-quote-mark">"</span>
              <p>When organisations have the right tools, they don't just manage data better, but they deliver better services. And that is the ultimate goal.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function About() {
  const cards = [
    { icon: UserCog, title: 'Built by practitioners', body: 'Designed by professionals who have worked directly in social impact organisations. We understand the real challenges from the inside, not from a boardroom.' },
    { icon: CheckCircle2, title: 'Proven product', body: 'Already live and operational, supporting an active youth empowerment programme. Not a concept but a working system with a real track record.' },
    { icon: Coins, title: 'Affordable and efficient', body: 'The CareTrack Case Management System is very affordable and performs with high efficiency, giving your organisation maximum value without unnecessary complexity.' },
    { icon: Rocket, title: 'Structured deployment', body: 'From agreement to going live in 3 to 6 weeks. We configure the system to your exact workflows carefully so your team can start working with confidence.' },
    { icon: Cloud, title: 'Cloud-based', body: 'Accessible from any device with a browser. No special hardware, no installation, and no IT department needed.' },
    { icon: Lock, title: 'Secure data', body: 'All data is stored on encrypted cloud servers with automated backups. Role-based access ensures only authorised staff see what they need to see.' },
  ];
  return (
    <section className="section about-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">Who we are</div>
            <h2 className="section-title">About CareTrack Ghana</h2>
            <div className="gold-line" />
            <p className="section-desc">CareTrack Ghana is a technology company building modern case management systems for organisations working at the heart of social impact. Our platform is designed for NGOs, foundations, and schools, with a focus on simplicity, affordability, and real-world usability.</p>
          </div>
        </Reveal>
        <div className="about-grid">
          {cards.map((c, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="about-card">
                <div className="about-icon"><c.icon size={26} strokeWidth={1.75} /></div>
                <div className="about-card-title">{c.title}</div>
                <div className="about-card-body">{c.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <div className="about-banner">
            <div className="about-banner-title">Who we serve</div>
            <p>Child welfare NGOs · Youth development programmes · Women's rights organisations · Disability support groups · Faith-based welfare organisations · International development organisations · Schools and educational foundations · Community-based foundations</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HowItWorks({ setActive }) {
  const steps = [
    { num: '01', icon: Phone, title: 'Contact us', body: 'Reach out via email, phone, or WhatsApp. Tell us about your organisation and the programmes you run. We will listen carefully.' },
    { num: '02', icon: Target, title: 'Free demo', body: 'We schedule a live demonstration of the CareTrack system, tailored to your specific programme structure and workflow.' },
    { num: '03', icon: Settings, title: 'Customisation', body: 'We customise the system to match your organisation\'s name, branding, programme components, and data fields.' },
    { num: '04', icon: Rocket, title: 'Go live', body: 'We deploy your system, train your staff, and provide ongoing support. You are up and running within 3 to 6 weeks.' },
  ];
  return (
    <section className="section how-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">The process</div>
            <h2 className="section-title">How it works</h2>
            <div className="gold-line" />
            <p className="section-desc">Getting started with CareTrack Ghana is simple. From first contact to going live takes 3 to 6 weeks.</p>
          </div>
        </Reveal>
        <div className="how-grid">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="how-card">
                <div className="how-num">{s.num}</div>
                <div className="how-icon"><s.icon size={28} strokeWidth={1.75} /></div>
                <div className="how-title">{s.title}</div>
                <div className="how-body">{s.body}</div>
                {i < steps.length - 1 && <div className="how-arrow">→</div>}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={300}>
          <div className="how-cta">
            <button className="btn-primary" onClick={() => setActive('Contact')}>Start the process today →</button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ── Sector Demo Data ──
const DEMO_COMPONENTS = [
  {id:1,name:"Sports for Development",icon:"🏃",color:"#E74C3C",light:"#FDEDEC",count:18},
  {id:2,name:"Youth Leadership & Mentorship",icon:"🤝",color:"#2980B9",light:"#EBF5FB",count:24},
  {id:3,name:"Entrepreneurship & Skills",icon:"🛠️",color:"#F39C12",light:"#FEF9E7",count:15},
  {id:4,name:"Education Support",icon:"📚",color:"#27AE60",light:"#EAFAF1",count:31},
  {id:5,name:"Health & Wellness",icon:"🏥",color:"#E67E22",light:"#FEF5E7",count:12},
  {id:6,name:"Disability Inclusion",icon:"🫂",color:"#8E44AD",light:"#F5EEF8",count:8},
  {id:7,name:"Environmental & Community",icon:"♻️",color:"#16A085",light:"#E8F8F5",count:16},
];

const DEMO_BENS = [
  {id:"1",bid:"CYEP-001",name:"Beneficiary 1",age:19,gender:"Male",community:"New Juaben North",component_id:4,status:"Active",assigned_to:"officer-1",last_follow_up:"2026-06-01"},
  {id:"2",bid:"CYEP-002",name:"Beneficiary 2",age:22,gender:"Female",community:"Effiduase",component_id:2,status:"Active",assigned_to:"officer-1",last_follow_up:"2026-05-28"},
  {id:"3",bid:"CYEP-003",name:"Beneficiary 3",age:17,gender:"Male",community:"Asokore",component_id:1,status:"Completed",assigned_to:"officer-2",last_follow_up:"2026-05-15"},
  {id:"4",bid:"CYEP-004",name:"Beneficiary 4",age:25,gender:"Female",community:"Oyoko",component_id:3,status:"Active",assigned_to:"officer-2",last_follow_up:"2026-06-03"},
  {id:"5",bid:"CYEP-005",name:"Beneficiary 5",age:20,gender:"Male",community:"Kukurantumi",component_id:5,status:"On Hold",assigned_to:"officer-1",last_follow_up:null},
];

const DEMO_POSTS = [
  {id:"p1",author:"Kofi Mensah",date:"2026-06-01",text:"Home visit conducted. Beneficiary is actively participating in the Education Support component. Parents are supportive and engagement has improved significantly since last visit.",comments:[
    {id:"c1",author:"Mark Effah Yeboah",author_role:"Admin",text:"Great progress. Please schedule a follow-up in two weeks and document any changes in school attendance."},
    {id:"c2",author:"Kofi Mensah",author_role:"Programme Officer",text:"Understood. Will visit again on June 15 and update the record."},
  ]},
  {id:"p2",author:"Kofi Mensah",date:"2026-05-10",text:"Initial assessment completed. Beneficiary enrolled in Education Support component. SIR submitted for review. No health concerns noted at this time.",comments:[]},
];

const SECTOR_DATA = {
  ngo: { sidebarBg:"linear-gradient(180deg,#1E8449 0%,#27AE60 100%)", orgName:"Hope Foundation Ghana", orgSub:"Community Programme", url:"caretrack.hopefoundation.org", navItems:["Dashboard","Beneficiaries","SIR Reports","Posts","My Account","User Mgmt","Settings"], activeNav:"Dashboard" },
  school: { sidebarBg:"linear-gradient(180deg,#1A5276 0%,#2980B9 100%)", orgName:"GIS Koforidua", orgSub:"Student Welfare", url:"caretrack.ghanaintlschool.edu.gh", navItems:["Dashboard","Students","Timetable","Case Reports","My Account","User Mgmt","Settings"], activeNav:"Dashboard" },
};

const SECTOR_INFO = {
  ngo: { label:"NGOs & Foundations", tagline:"Built for the heart of social impact work.", description:"Whether you run a child welfare programme, a youth empowerment initiative, or a community development organisation, CareTrack gives your team a single platform to manage every beneficiary from first contact to final outcome. No more scattered notebooks, lost case files, or forgotten follow-ups.", points:["Register and track beneficiaries across multiple programme components","Log follow-up notes and comment threads for every case","Generate Social Inquiry Reports automatically in seconds","Monitor programme performance with a live dashboard","Manage staff roles and access levels with ease"], cta:"Request a free NGO demo", color:"#27AE60" },
  school: { label:"Schools & Educational Institutions", tagline:"Every student's welfare, tracked and never forgotten.", description:"Schools deal with complex student welfare situations every day, from counselling referrals and behavioural cases to attendance concerns and family issues. CareTrack gives your pastoral care team a structured, confidential system to manage every case properly and professionally.", points:["Log and track student welfare cases from referral to resolution","Monitor counselling sessions, interventions, and outcomes","Flag attendance concerns and link them to welfare cases","Generate reports for school management and external bodies","Control access so only authorised staff see sensitive records"], cta:"Request a free school demo", color:"#2980B9" },
};

const SECTOR_ICONS = ["🏠","👥","📋","📝","💬","👤","👥","🔧","🗺️","📊"];

function SectorSidebar({ sector }) {
  const d = SECTOR_DATA[sector];
  return (
    <div style={{width:165,background:d.sidebarBg,display:"flex",flexDirection:"column",flexShrink:0}}>
      <div style={{padding:"12px",borderBottom:"1px solid rgba(255,255,255,0.15)"}}>
        <div style={{fontFamily:"'Poppins',sans-serif",fontSize:12,fontWeight:700,color:"#fff"}}>{d.orgName}</div>
        <div style={{fontSize:9,color:"rgba(255,255,255,0.5)",letterSpacing:1.5,textTransform:"uppercase",marginTop:2}}>{d.orgSub}</div>
      </div>
      <div style={{padding:"8px 6px",flex:1}}>
        {d.navItems.map((item,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:7,padding:"6px 8px",borderRadius:5,fontSize:11,cursor:"pointer",marginBottom:1,color:item===d.activeNav?"#fff":"rgba(255,255,255,0.72)",fontWeight:item===d.activeNav?600:400,background:item===d.activeNav?"rgba(255,255,255,0.18)":"transparent"}}>
            <span style={{fontSize:11}}>{SECTOR_ICONS[i]||"📌"}</span><span>{item}</span>
          </div>
        ))}
      </div>
      <div style={{padding:"10px 12px",borderTop:"1px solid rgba(255,255,255,0.15)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:26,height:26,borderRadius:"50%",background:"rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:10,color:"#fff"}}>MY</div>
          <div><div style={{color:"#fff",fontSize:10,fontWeight:700}}>Mark Effah Yeboah</div><div style={{fontSize:9,color:"rgba(255,255,255,0.6)"}}>Admin</div></div>
        </div>
      </div>
    </div>
  );
}

function NGODashboard() {
  return (
    <div style={{flex:1,overflowY:"auto",background:"#F7FAFC"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e2e8f0",padding:"0 16px",height:42,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div><div style={{fontSize:13,fontWeight:700,color:"#1A252F"}}>Dashboard</div><div style={{fontSize:9,color:"#718096"}}>Community Youth Empowerment Programme</div></div>
        <div style={{fontSize:9,color:"#718096"}}>June 10, 2026</div>
      </div>
      <div style={{padding:"10px 14px"}}>
        <div style={{fontSize:9,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:1.5,marginBottom:7}}>Programme summary</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:7,marginBottom:12}}>
          {[["#1A252F","124","Total"],["#27AE60","89","Active"],["#2980B9","23","Completed"],["#8E44AD","68","Male"],["#E67E22","56","Female"]].map(([bg,n,l],i)=>(
            <div key={i} style={{background:bg,borderRadius:8,padding:"8px 4px",textAlign:"center"}}>
              <div style={{fontSize:22,fontWeight:700,color:"#fff",lineHeight:1}}>{n}</div>
              <div style={{fontSize:8,color:"rgba(255,255,255,0.8)",textTransform:"uppercase",letterSpacing:0.8,marginTop:3}}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{fontSize:9,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:1.5,marginBottom:7}}>By programme component</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7}}>
          {[["#E74C3C","🏃","Sports Dev","18"],["#2980B9","🤝","Youth Leadership","24"],["#F39C12","🛠️","Entrepreneurship","15"],["#27AE60","📚","Education","31"],["#E67E22","🏥","Health","12"],["#8E44AD","🫂","Disability","8"],["#16A085","♻️","Environment","16"]].map(([bg,ic,nm,n],i)=>(
            <div key={i} style={{background:bg,borderRadius:8,padding:"8px 6px",textAlign:"center"}}>
              <div style={{fontSize:13,marginBottom:3}}>{ic}</div>
              <div style={{fontSize:8,color:"rgba(255,255,255,0.9)",fontWeight:600,marginBottom:4,lineHeight:1.3}}>{nm}</div>
              <div style={{fontSize:20,fontWeight:700,color:"#fff",lineHeight:1}}>{n}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SchoolDashboard() {
  return (
    <div style={{flex:1,overflowY:"auto",background:"#F7FAFC"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e2e8f0",padding:"0 16px",height:42,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div><div style={{fontSize:13,fontWeight:700,color:"#1A252F"}}>Student welfare dashboard</div><div style={{fontSize:9,color:"#718096"}}>Ghana International School</div></div>
        <div style={{fontSize:9,color:"#718096"}}>June 10, 2026</div>
      </div>
      <div style={{padding:"10px 14px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div>
          <div style={{fontSize:9,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:1.5,marginBottom:7}}>Overview</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:10}}>
            {[["#1A252F","847","Total students"],["#2980B9","43","Active cases"],["#27AE60","112","Resolved"],["#E74C3C","8","Urgent"]].map(([bg,n,l],i)=>(
              <div key={i} style={{background:bg,borderRadius:7,padding:"8px 6px",textAlign:"center"}}>
                <div style={{fontSize:20,fontWeight:700,color:"#fff",lineHeight:1}}>{n}</div>
                <div style={{fontSize:8,color:"rgba(255,255,255,0.8)",textTransform:"uppercase",letterSpacing:0.5,marginTop:3}}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:9,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:1.5,marginBottom:7}}>Intervention types</div>
          {[["Counselling",18,"#2980B9"],["Academic support",24,"#27AE60"],["Behavioural",9,"#E74C3C"],["Family issues",14,"#F39C12"],["Health referral",7,"#E67E22"]].map(([l,n,c],i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:5,marginBottom:5}}>
              <div style={{fontSize:9,color:"#2a4365",width:95,flexShrink:0}}>{l}</div>
              <div style={{flex:1,height:5,background:"#e2e8f0",borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.round(n/24*100)}%`,background:c,borderRadius:3}}/></div>
              <div style={{fontSize:9,color:"#718096",width:16,textAlign:"right"}}>{n}</div>
            </div>
          ))}
        </div>
        <div>
          <div style={{fontSize:9,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:1.5,marginBottom:7}}>Recent student cases</div>
          <div style={{background:"#fff",border:"0.5px solid #e2e8f0",borderRadius:7,overflow:"hidden",marginBottom:8}}>
            {[["Student 1","Form 2A","Active","#EAFAF1","#1D8348"],["Student 2","JHS 3B","Pending","#FEF9E7","#9A7D0A"],["Student 3","Form 1C","Active","#EAFAF1","#1D8348"],["Student 4","SHS 2A","Resolved","#EBF5FB","#1A5276"],["Student 5","Form 3B","Active","#EAFAF1","#1D8348"]].map(([n,c,s,sbg,sc],i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"5px 8px",borderBottom:"0.5px solid #f0f4f8"}}>
                <div style={{fontSize:10,color:"#1a252f",fontWeight:500}}>{n}</div>
                <div style={{fontSize:9,color:"#718096"}}>{c}</div>
                <span style={{background:sbg,color:sc,padding:"1px 6px",borderRadius:20,fontSize:8,fontWeight:600}}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function DemoBenList({onView}) {
  const compFor = id => DEMO_COMPONENTS.find(c=>c.id===id);
  return (
    <div style={{flex:1,overflowY:"auto",background:"#F7FAFC"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e2e8f0",padding:"0 16px",height:42,display:"flex",alignItems:"center"}}>
        <div><div style={{fontSize:13,fontWeight:700,color:"#1A252F"}}>Beneficiaries</div><div style={{fontSize:9,color:"#718096"}}>View and manage all beneficiary profiles</div></div>
      </div>
      <div style={{padding:"10px 14px"}}>
        <div style={{background:"#fff",borderRadius:7,padding:"8px 10px",marginBottom:10,border:"1px solid #e2e8f0",display:"flex",gap:7,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div style={{flex:"1 1 100px"}}><div style={{fontSize:8,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:0.8,marginBottom:3}}>Search</div><div style={{border:"1px solid #CBD5E0",borderRadius:5,padding:"5px 8px",fontSize:10,color:"#718096"}}>Name or ID...</div></div>
          <div style={{flex:"1 1 90px"}}><div style={{fontSize:8,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:0.8,marginBottom:3}}>Component</div><div style={{border:"1px solid #CBD5E0",borderRadius:5,padding:"5px 8px",fontSize:10,color:"#718096"}}>All</div></div>
          <div style={{flex:"1 1 70px"}}><div style={{fontSize:8,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:0.8,marginBottom:3}}>Status</div><div style={{border:"1px solid #CBD5E0",borderRadius:5,padding:"5px 8px",fontSize:10,color:"#718096"}}>All</div></div>
          <div style={{background:"#27AE60",color:"#fff",padding:"5px 12px",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer"}}>Search</div>
        </div>
        <div style={{background:"#fff",borderRadius:7,overflow:"hidden",border:"1px solid #e2e8f0"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#F7FAFC",borderBottom:"2px solid #e2e8f0"}}>
              {["Beneficiary","Component","Community","Officer","Last Follow-Up","Status","Actions"].map(h=>(
                <th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:8,fontWeight:700,color:"#2A4365",textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {DEMO_BENS.map((b,i)=>{
                const c = compFor(b.component_id);
                const ss = b.status==="Active"?{bg:"#EAFAF1",color:"#1D8348"}:b.status==="Completed"?{bg:"#EBF5FB",color:"#1A5276"}:{bg:"#FEF9E7",color:"#9A7D0A"};
                return (
                  <tr key={b.id} style={{background:i%2===0?"#fff":"#F7FAFC",borderBottom:"1px solid #EDF2F7"}}>
                    <td style={{padding:"8px 10px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:7}}>
                        <div style={{width:28,height:28,borderRadius:"50%",background:c?.color||"#27AE60",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:10,color:"#fff",flexShrink:0}}>{i+1}</div>
                        <div><div style={{fontWeight:700,fontSize:11,color:"#1A252F"}}>{b.name}</div><div style={{fontSize:9,color:"#718096"}}>Age {b.age} · {b.gender}</div></div>
                      </div>
                    </td>
                    <td style={{padding:"8px 10px"}}>{c&&<span style={{background:c.light,color:c.color,padding:"2px 7px",borderRadius:20,fontSize:9,fontWeight:700}}>{c.icon} {c.name.split(" ")[0]}</span>}</td>
                    <td style={{padding:"8px 10px",fontSize:10,color:"#1A252F"}}>{b.community}</td>
                    <td style={{padding:"8px 10px",fontSize:10,color:"#1A252F"}}>{b.assigned_to==="officer-1"?"Kofi Mensah":"Abena Owusu"}</td>
                    <td style={{padding:"8px 10px",fontSize:10,color:b.last_follow_up?"#C0392B":"#718096",fontWeight:b.last_follow_up?600:400}}>{b.last_follow_up||"Not yet"}</td>
                    <td style={{padding:"8px 10px"}}><span style={{background:ss.bg,color:ss.color,padding:"2px 8px",borderRadius:20,fontSize:9,fontWeight:700}}>{b.status}</span></td>
                    <td style={{padding:"8px 10px"}}>
                      <div style={{display:"flex",gap:3}}>
                        <button onClick={()=>onView(b)} style={{padding:"3px 7px",borderRadius:4,border:"none",cursor:"pointer",fontSize:9,fontWeight:700,background:"#EBF5FB",color:"#1A5276"}}>👁 View</button>
                        <button style={{padding:"3px 7px",borderRadius:4,border:"none",cursor:"pointer",fontSize:9,fontWeight:700,background:"#EAFAF1",color:"#1D8348"}}>✏️ Edit</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DemoProfile({ben, onBack}) {
  const [tab, setTab] = useState("basic");
  const comp = DEMO_COMPONENTS.find(c=>c.id===ben.component_id);
  const tabs = [{k:"basic",label:"Basic Info",icon:"👤"},{k:"programme",label:"Programme",icon:"📌"},{k:"health",label:"Health",icon:"🏥"},{k:"education",label:"Education",icon:"📚"},{k:"family",label:"Family",icon:"🏠"},{k:"employment",label:"Employment",icon:"💼"},{k:"disability",label:"Disability",icon:"🫂"},{k:"followups",label:"Follow-Ups",icon:"💬"},{k:"documents",label:"Documents",icon:"📁"}];
  const FBox = ({label,value}) => (
    <div><div style={{fontSize:9,fontWeight:700,color:"#718096",textTransform:"uppercase",letterSpacing:0.8,marginBottom:3}}>{label}</div>
    <div style={{background:"#F7FAFC",border:"1px solid #CBD5E0",borderRadius:6,padding:"7px 10px",fontSize:11,color:"#1A252F"}}>{value||"—"}</div></div>
  );
  return (
    <div style={{flex:1,overflowY:"auto",background:"#F7FAFC"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e2e8f0",padding:"0 16px",height:42,display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onBack} style={{padding:"4px 10px",borderRadius:6,border:"1px solid #e2e8f0",cursor:"pointer",fontSize:11,fontWeight:600,background:"#F7FAFC",color:"#1A252F"}}>← Back</button>
        <div><div style={{fontSize:13,fontWeight:700,color:"#1A252F"}}>{ben.name}</div><div style={{fontSize:9,color:"#718096"}}>Profile · {ben.bid}</div></div>
      </div>
      <div style={{padding:"12px 14px"}}>
        <div style={{display:"grid",gridTemplateColumns:"180px 1fr",gap:12}}>
          <div style={{background:"#fff",borderRadius:10,overflow:"hidden",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
            <div style={{background:comp?.color||"#27AE60",padding:"16px 12px",textAlign:"center"}}>
              <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:16,color:"#fff",margin:"0 auto 8px"}}>{ben.id}</div>
              <div style={{fontSize:12,fontWeight:700,color:"#fff"}}>{ben.name}</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,0.85)",marginTop:2}}>{comp?.icon} {comp?.name}</div>
              <div style={{marginTop:8}}><span style={{background:"rgba(255,255,255,0.25)",color:"#fff",padding:"2px 10px",borderRadius:20,fontSize:10,fontWeight:700}}>{ben.status}</span></div>
            </div>
            <div style={{padding:"0 10px 10px"}}>
              {[["ID",ben.bid],["Age/Gender",`${ben.age} · ${ben.gender}`],["Community",ben.community],["Officer",ben.assigned_to==="officer-1"?"Kofi":"Abena"]].map(([l,v])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #EDF2F7"}}>
                  <span style={{fontSize:9,fontWeight:700,color:"#718096"}}>{l}</span>
                  <span style={{fontSize:9,color:"#1A252F",textAlign:"right"}}>{v||"—"}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{display:"flex",flexWrap:"wrap",gap:2,background:"#EDF2F7",borderRadius:8,padding:3,marginBottom:10}}>
              {tabs.map(t=>(
                <button key={t.k} onClick={()=>setTab(t.k)} style={{padding:"5px 8px",borderRadius:5,border:"none",cursor:"pointer",fontSize:9,display:"flex",alignItems:"center",gap:3,background:tab===t.k?"#fff":"transparent",color:tab===t.k?"#1A252F":"#718096",fontWeight:tab===t.k?700:400,boxShadow:tab===t.k?"0 1px 3px rgba(0,0,0,0.1)":"none"}}>
                  <span>{t.icon}</span><span>{t.label}</span>
                </button>
              ))}
            </div>
            <div style={{background:"#fff",borderRadius:10,padding:"14px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
              {tab==="basic"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[["Full Name",ben.name],["Age",ben.age],["Gender",ben.gender],["Nationality","Ghanaian"],["Religion","Christian"],["Community",ben.community],["District","New Juaben North"],["Region","Eastern"]].map(([l,v])=><FBox key={l} label={l} value={v}/>)}
              </div>}
              {tab==="programme"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[["Beneficiary ID",ben.bid],["Component",`${comp?.icon} ${comp?.name}`],["Enrolment Date","2026-01-15"],["Status",ben.status]].map(([l,v])=><FBox key={l} label={l} value={v}/>)}
              </div>}
              {tab==="health"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[["Health Status","Good"],["Disability","N/A"],["Medical Condition","None"]].map(([l,v])=><FBox key={l} label={l} value={v}/>)}
              </div>}
              {tab==="followups"&&<div>
                <button style={{background:"#27AE60",color:"#fff",border:"none",borderRadius:6,padding:"7px 14px",fontSize:11,fontWeight:700,cursor:"pointer",marginBottom:12}}>+ Add Follow-Up Note</button>
                {DEMO_POSTS.map(p=>(
                  <div key={p.id} style={{background:"#F7FAFC",borderRadius:10,padding:"10px",marginBottom:10,border:"1px solid #e2e8f0"}}>
                    <div style={{display:"flex",gap:8,marginBottom:8}}>
                      <div style={{width:28,height:28,borderRadius:"50%",background:"#2980B9",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:9,color:"#fff",flexShrink:0}}>KM</div>
                      <div style={{flex:1}}><div style={{fontSize:9,color:"#718096",marginBottom:3}}>{p.author} · {p.date}</div><div style={{fontSize:10,color:"#1A252F",lineHeight:1.5,background:"#EBF5FB",borderRadius:"0 7px 7px 7px",padding:"6px 8px"}}>{p.text}</div></div>
                    </div>
                    {p.comments.map(c=>(
                      <div key={c.id} style={{display:"flex",gap:6,marginBottom:6,marginLeft:34,flexDirection:c.author_role==="Admin"?"row-reverse":"row"}}>
                        <div style={{width:24,height:24,borderRadius:"50%",background:c.author_role==="Admin"?"#1A252F":"#2980B9",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:8,color:"#fff",flexShrink:0}}>{c.author_role==="Admin"?"MY":"KM"}</div>
                        <div style={{flex:1}}><div style={{fontSize:8,color:"#718096",marginBottom:2,textAlign:c.author_role==="Admin"?"right":"left"}}>{c.author}</div><div style={{fontSize:10,color:"#1A252F",lineHeight:1.5,background:c.author_role==="Admin"?"#EAFAF1":"#fff",borderRadius:c.author_role==="Admin"?"7px 0 7px 7px":"0 7px 7px 7px",padding:"5px 8px",border:"1px solid #e2e8f0"}}>{c.text}</div></div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>}
              {(tab==="education"||tab==="family"||tab==="employment"||tab==="disability"||tab==="documents")&&<div style={{textAlign:"center",padding:"20px 0",color:"#718096",fontSize:12}}>Select a tab to view details</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppDemo({ setActive }) {
  const [sector, setSector] = useState("ngo");
  const [demoPage, setDemoPage] = useState("dashboard");
  const [selectedBen, setSelectedBen] = useState(null);

  const sectorTabs = [
    {id:"ngo",label:"NGO / Foundation"},
    {id:"school",label:"School"},
  ];

  const ngoAppTabs = [
    {id:"dashboard",label:"📊 Dashboard"},
    {id:"benlist",label:"👥 Beneficiary List"},
  ];

  const handleSectorChange = (s) => { setSector(s); setDemoPage("dashboard"); setSelectedBen(null); };
  const handleView = (ben) => { setSelectedBen(ben); setDemoPage("profile"); };
  const handleBack = () => { setSelectedBen(null); setDemoPage("benlist"); };

  const renderDashboard = () => {
    switch(sector) {
      case "school": return <SchoolDashboard />;
      default: return <NGODashboard />;
    }
  };

  const renderMain = () => {
    if (sector !== "ngo") return renderDashboard();
    if (demoPage === "profile" && selectedBen) return <DemoProfile ben={selectedBen} onBack={handleBack}/>;
    if (demoPage === "benlist") return <DemoBenList onView={handleView}/>;
    return <NGODashboard />;
  };

  const info = SECTOR_INFO[sector];

  return (
    <div className="app-demo-wrap">
      <div className="demo-sector-bar">
        <span className="demo-sector-label">Select your sector:</span>
        {sectorTabs.map(t=>(
          <button key={t.id} onClick={()=>handleSectorChange(t.id)} className={`demo-sector-btn ${sector===t.id?"demo-sector-active":""}`}>{t.label}</button>
        ))}
      </div>
      {sector === "ngo" && (
        <div className="app-demo-tabs">
          {ngoAppTabs.map(t=>(
            <button key={t.id} onClick={()=>{setSelectedBen(null);setDemoPage(t.id);}} className={`demo-tab ${demoPage===t.id?"demo-tab-active":""}`}>{t.label}</button>
          ))}
        </div>
      )}
      <div className={`app-demo-browser ${sector!=="ngo"?"demo-browser-no-tabs":""}`}>
        <div className="demo-browser-bar">
          <div className="demo-browser-dots"><span/><span/><span/></div>
          <div className="demo-browser-url">{SECTOR_DATA[sector].url}</div>
          <div className="demo-browser-secure">🔒 Secure</div>
        </div>
        <div className="demo-browser-body">
          <SectorSidebar sector={sector}/>
          {renderMain()}
        </div>
      </div>
      <div className="demo-sector-info">
        <div className="demo-sector-info-left">
          <div className="demo-sector-info-label" style={{color:info.color}}>{info.label}</div>
          <div className="demo-sector-info-tagline">"{info.tagline}"</div>
          <p className="demo-sector-info-desc">{info.description}</p>
          <ul className="demo-sector-points">
            {info.points.map((p,i)=>(
              <li key={i}>
                <span className="demo-sector-check" style={{color:info.color}}><Check size={14} strokeWidth={3} /></span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="demo-sector-info-right">
          <div className="demo-sector-cta-box" style={{borderColor:info.color}}>
            <div className="demo-sector-cta-title">Does this look right for your organisation?</div>
            <p className="demo-sector-cta-body">We will customise CareTrack specifically for your {info.label.toLowerCase()}: your name, your branding, your programmes. Book a free demonstration and see it built for you.</p>
            <a href={`mailto:info@caretrackghana.com?subject=${info.cta} — CareTrack Ghana`} className="btn-primary demo-cta-btn" style={{background:info.color}}>
              {info.cta} →
            </a>
            {sector === "ngo" && (
              <div className="demo-sector-nav-tip">💡 Click the tabs above to switch between Dashboard and Beneficiary List. Inside the list, click "👁 View" on any beneficiary to open their full profile and follow-up thread.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Features({ setActive }) {
  const features = [
    { icon: Users, title: 'Beneficiary Management', body: 'Register and manage complete beneficiary profiles: personal details, household information, programme enrolment, and full case history in one place.' },
    { icon: ClipboardList, title: 'Programme Component Tracking', body: 'Track beneficiary progress across multiple programme components and activities. Know exactly where every person stands at any time.' },
    { icon: RefreshCw, title: 'Follow-Up Thread Management', body: 'Every follow-up action is logged in a threaded record attached to the beneficiary. Nothing falls through the cracks.' },
    { icon: FileText, title: 'SIR Generation', body: 'Generate Social Inquiry Reports automatically in seconds. Save hours of manual documentation per case.' },
    { icon: BarChart3, title: 'Live Dashboard', body: 'A real-time programme overview showing beneficiary counts, component performance, and activity statistics, accessible from any device.' },
    { icon: UserCircle, title: 'User Role Management', body: 'Assign different access levels to programme staff, supervisors, and administrators. Control who sees what.' },
    { icon: PenLine, title: 'Posts and Updates', body: 'Share programme updates, announcements, and notices with your team directly inside the system.' },
    { icon: Settings, title: 'My Account', body: 'Each staff member manages their own profile, password, and notification preferences within their account.' },
  ];
  return (
    <section className="section features-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">What you get</div>
            <h2 className="section-title">See CareTrack in action</h2>
            <div className="gold-line" />
            <p className="section-desc">Explore the actual CareTrack Data Management System below. This is a sample of what your organisation will use. It can be customised with your organisation's name, branding, programme components etc.</p>
            <a href="https://demo.caretrackghana.com" target="_blank" rel="noopener noreferrer" className="live-demo-link">
              <ExternalLink size={15} strokeWidth={2} /> See the real live app at demo.caretrackghana.com
            </a>
            <p className="live-demo-creds">Login with <strong>trial@caretrackghana.com</strong> / <strong>Trial@123</strong> to explore with sample beneficiaries and cases.</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <AppDemo setActive={setActive} />
        </Reveal>
        <div className="features-grid">
          {features.map((f, i) => (
            <Reveal key={i} delay={i * 60}>
              <div className="feat-card">
                <div className="feat-icon"><f.icon size={24} strokeWidth={1.75} /></div>
                <div className="feat-title">{f.title}</div>
                <div className="feat-body">{f.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const testimonials = [
    { quote: "Before CareTrack, we were managing over 200 beneficiaries on paper files and Excel sheets. Now everything is in one place and our reporting to donors has become so much easier.", name: "Programme Director", org: "Youth Empowerment Foundation, Eastern Region", gradient: "linear-gradient(135deg,#27AE60,#1E8449)" },
    { quote: "The system was customised to match our exact programme structure within weeks. The team understood our work immediately, because they have actually done social work themselves.", name: "Executive Director", org: "Child Welfare NGO, Greater Accra", gradient: "linear-gradient(135deg,#2980B9,#1A5276)" },
    { quote: "Generating Social Inquiry Reports used to take our social workers hours. With CareTrack it takes minutes. The time we save goes directly back into serving our beneficiaries.", name: "Head of Programmes", org: "Community Development Organisation, Ashanti Region", gradient: "linear-gradient(135deg,#F39C12,#B9770E)" },
  ];
  return (
    <section className="section testimonials-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">What they say</div>
            <h2 className="section-title">Trusted by organisations that serve people</h2>
            <div className="gold-line" />
          </div>
        </Reveal>
        <div className="testimonials-grid">
          {testimonials.map((t, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="testimonial-card">
                <div className="testimonial-stars">{[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" strokeWidth={0} />)}</div>
                <div className="testimonial-quote">"{t.quote}"</div>
                <div className="testimonial-author">
                  <div className="testimonial-avatar" style={{background: t.gradient}}><UserCircle size={24} color="#fff" strokeWidth={1.5} /></div>
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-org">{t.org}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function DataOwnership({ setActive }) {
  const cards = [
    { icon: ShieldCheck, title: 'Security You Can Trust', body: 'Every CareTrack system is built so that only the staff an organisation authorises can ever see or change its records. Beneficiary information, including sensitive details and photos, is protected and never publicly accessible. Each deployment is independently security-tested before handover. We verify, we do not just promise.' },
    { icon: Landmark, title: 'You Own Your Data', body: "The organisation's data belongs to the organisation, always. The system can be set up under the client's own accounts so they retain full ownership, whilst we handle the technical management and support. Their records are theirs to keep, export, and control." },
    { icon: Unlock, title: 'Never Locked In', body: "Clients are never trapped with us. The system is built under the organisation's own accounts, meaning the app and all its data remain fully functional even if the working relationship ends. What they lose is CareTrack's ongoing support, updates, and security patches. However, the platform itself continues to serve them." },
  ];
  return (
    <section className="section data-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">Data governance</div>
            <h2 className="section-title">Your Data, Your Control</h2>
            <div className="gold-line" />
            <p className="section-desc">When organisations trust CareTrack with their most sensitive records, they deserve to know exactly who can see them, who owns them, and what happens if circumstances ever change.</p>
          </div>
        </Reveal>
        <div className="data-grid">
          {cards.map((c, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="data-card">
                <div className="data-icon"><c.icon size={22} color="#fff" /></div>
                <div className="data-title">{c.title}</div>
                <div className="data-body">{c.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <p className="data-cta">
            Questions about our security and data-ownership approach? Bring them to your{' '}
            <button className="link-btn" onClick={() => setActive('Contact')}>free demo</button>{' '}
            and we will walk through everything together.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Pricing({ setActive }) {
  const tiers = [
    { icon: HandHeart, name: 'NGOs & Foundations', desc: 'Child welfare, youth development, women\'s rights, disability support, faith-based organisations', setup: 'GH₵15,000 – GH₵17,000', monthly: 'GH₵1,000 / month', featured: true, badge: 'High Demand', perks: ['Full system setup & customisation','Staff onboarding & training','Monthly technical support','System updates included','Cloud hosting included'] },
    { icon: GraduationCap, name: 'Schools & Educational Institutions', desc: 'Student welfare tracking, counselling case management, intervention and progress monitoring', setup: 'GH₵5,000 – GH₵7,000', monthly: 'GH₵1,000 / month', featured: false, perks: ['Full system setup & customisation','Staff onboarding & training','Priority monthly support','System updates included','Cloud hosting included'] },
  ];
  return (
    <section className="section pricing-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">What it costs</div>
            <h2 className="section-title">Pricing for every sector</h2>
            <div className="gold-line" />
            <p className="section-desc">One-time setup fee covers full customisation, onboarding, and training. Monthly fee covers support, hosting, and updates. No hidden charges.</p>
          </div>
        </Reveal>
        <div className="pricing-grid-new">
          {tiers.map((t, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className={`price-card-new ${t.featured ? 'featured' : ''} ${t.enterprise ? 'enterprise' : ''}`}>
                <div className="price-card-top">
                  <div className="price-sector-icon"><t.icon size={28} strokeWidth={1.75} /></div>
                  <div>
                    {t.badge && <div className="price-badge">{t.badge}</div>}
                    <div className="price-tier">{t.name}</div>
                    <div className="price-desc">{t.desc}</div>
                  </div>
                </div>
                <div className="price-amounts">
                  <div>
                    <div className="price-amount">{t.setup}</div>
                    <div className="price-setup-lbl">{t.enterprise ? '' : 'one-time setup'}</div>
                  </div>
                  <div className="price-monthly-new">+ {t.monthly}</div>
                </div>
                <hr className="price-divider" />
                <ul className="price-perks">
                  {t.perks.map((p, j) => (<li key={j}><span className="price-check"><Check size={13} strokeWidth={3} /></span> {p}</li>))}
                </ul>
                <button className={t.featured || t.enterprise ? 'btn-primary full-width' : 'btn-outline-dark full-width'} onClick={() => setActive('Contact')}>
                  {t.enterprise ? 'Contact us →' : 'Get started →'}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <div className="pricing-note">All packages include system setup, staff training, cloud hosting, and ongoing support. <button className="link-btn" onClick={() => setActive('Contact')}>Contact us to discuss your needs.</button></div>
        </Reveal>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: "Do we need to install any software?", a: "No. CareTrack is entirely web-based. Your staff access it through any web browser on any device: desktop, laptop, tablet, or smartphone. No installation, no IT department required." },
    { q: "Can the system be customised for our specific programmes?", a: "Absolutely. Every organisation's programmes are different, and CareTrack is built to be customised. We configure the system to match your exact programme components, data fields, and workflows before you go live." },
    { q: "What happens to our data if we stop using the service?", a: "Your data belongs to you, always. If you ever decide to stop using CareTrack's services, the system and all its data remain yours. You can continue using the platform independently. The only thing you will no longer receive is CareTrack's ongoing technical support, system updates, and security patches. Without these, the system may gradually become more vulnerable over time, but it remains fully functional and entirely under your ownership." },
    { q: "How long does it take to set up and go live?", a: "For most organisations, setup and deployment takes between 3 to 6 weeks from the time we agree on the customisation details. This covers system configuration, data setup, staff training, and testing. Training typically takes one session of 2 to 3 hours." },
    { q: "Is our beneficiary data secure?", a: "Yes. All data is stored on secure, encrypted cloud servers with regular automated backups. Access is controlled through role-based permissions so each staff member only sees what they need to see." },
    { q: "Do you offer support after we go live?", a: "Yes. All packages include monthly technical support. Our team is reachable by email, phone, and WhatsApp. You will never be left to figure things out alone." },
    { q: "Can multiple staff members use the system at the same time?", a: "Yes. CareTrack is designed for teams. Multiple users can be logged in and working simultaneously with their own accounts and permission levels." },
    { q: "What if we outgrow our current package?", a: "You can upgrade your package at any time. We will adjust the system and support level to match your organisation's growth without any disruption to your ongoing work." },
  ];
  return (
    <section className="section faq-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">Common questions</div>
            <h2 className="section-title">Frequently asked questions</h2>
            <div className="gold-line" />
          </div>
        </Reveal>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <Reveal key={i} delay={i * 50}>
              <div className={`faq-item ${open === i ? 'faq-open' : ''}`} onClick={() => setOpen(open === i ? null : i)}>
                <div className="faq-question"><span>{f.q}</span><span className="faq-icon">{open === i ? '−' : '+'}</span></div>
                {open === i && <div className="faq-answer">{f.a}</div>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: '', organisation: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('sending');
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, { from_name: form.name, organisation: form.organisation, from_email: form.email, message: form.message }, EMAILJS_PUBLIC_KEY);
      setStatus('sent');
      setForm({ name: '', organisation: '', email: '', message: '' });
    } catch { setStatus('error'); }
  };
  return (
    <section className="section contact-section">
      <div className="section-inner">
        <Reveal>
          <div className="section-header">
            <div className="section-eyebrow">Reach us</div>
            <h2 className="section-title">Get in touch</h2>
            <div className="gold-line" />
            <p className="section-desc">Schedule a free demonstration or send us a message. We respond to all enquiries within 24 hours.</p>
          </div>
        </Reveal>
        <div className="contact-grid">
          <Reveal className="contact-info">
            {[
              { icon: Mail, label: 'Email', val: 'info@caretrackghana.com' },
              { icon: MessageCircle, label: 'WhatsApp', val: '+233 (0) 553 208 451' },
              { icon: MapPin, label: 'Location', val: '66 1st Osu Badu St, Accra' },
              { icon: Calendar, label: 'Free Demo', val: 'Available in person or virtually' },
            ].map((item, i) => (
              <div className="contact-item" key={i}>
                <div className="contact-icon-box"><item.icon size={20} strokeWidth={1.75} color="#fff" /></div>
                <div><div className="contact-item-label">{item.label}</div><div className="contact-item-val">{item.val}</div></div>
              </div>
            ))}
            <div className="demo-box">
              <div className="demo-box-title">Request a Free Demo</div>
              <p className="demo-box-body">See the CareTrack system in action, customised to your organisation's programmes. No commitment required.</p>
              <a href="mailto:info@caretrackghana.com?subject=Free Demo Request — CareTrack Ghana" className="btn-primary demo-email-btn">Request a Free Demo →</a>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="contact-form-wrap">
              <div className="contact-form-title">Send us a message</div>
              {status === 'sent' ? (
                <div className="form-success">
                  <div className="form-success-icon"><CheckCircle2 size={48} color="#27AE60" /></div>
                  <div className="form-success-title">Message sent!</div>
                  <p>Thank you for reaching out. We will get back to you within 24 hours.</p>
                  <button className="btn-primary" onClick={() => setStatus('idle')}>Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  {[
                    { name: 'name', label: 'Your name *', placeholder: 'e.g. Ama Owusu', type: 'text', required: true },
                    { name: 'organisation', label: 'Organisation', placeholder: 'e.g. Hope Foundation Ghana', type: 'text', required: false },
                    { name: 'email', label: 'Email address *', placeholder: 'e.g. info@hopefoundation.org', type: 'email', required: true },
                  ].map(f => (
                    <div className="form-field" key={f.name}>
                      <label className="form-label">{f.label}</label>
                      <input className="form-input" type={f.type} name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} required={f.required} />
                    </div>
                  ))}
                  <div className="form-field">
                    <label className="form-label">Message *</label>
                    <textarea className="form-input form-textarea" name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your organisation and what you need..." required />
                  </div>
                  {status === 'error' && <div className="form-error">Something went wrong. Please email us directly at info@caretrackghana.com</div>}
                  <button type="submit" className="btn-primary full-width" disabled={status === 'sending'}>{status === 'sending' ? 'Sending...' : 'Send Message →'}</button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Footer({ setActive }) {
  return (
    <footer className="footer">
      <div className="footer-accent-stripe" />
      <div className="footer-inner">
        <div className="footer-brand">
          <img src="/logo.png" alt="CareTrack Ghana" className="footer-logo" />
          <div className="footer-tagline">Transforming Institutions Through Technology</div>
        </div>
        <div className="footer-links">
          {['Home','Features','About','Pricing','Contact'].map(l => (
            <button key={l} className="footer-link" onClick={() => setActive(l)}>{l}</button>
          ))}
        </div>
        <div className="footer-contact">
          <span><Mail size={13} strokeWidth={1.75} color="#aaa" /> info@caretrackghana.com</span>
          <span><MessageCircle size={13} strokeWidth={1.75} color="#aaa" /> +233 (0) 553 208 451</span>
        </div>
        <div className="footer-copy">© 2026 CareTrack Ghana. All rights reserved. | Transforming Institutions Through Technology</div>
      </div>
    </footer>
  );
}

export default function App() {
  const getPageFromHash = () => {
    const hash = window.location.hash.replace('#', '');
    const valid = ['Home','About','Features','Pricing','Contact'];
    return valid.includes(hash) ? hash : 'Home';
  };

  const [active, setActive] = useState(getPageFromHash);
  const [scrollActive, setScrollActive] = useState('Home');

  const handleSetActive = (page) => {
    setActive(page);
    setScrollActive(page);
    const url = page === 'Home' ? '/' : `#${page}`;
    window.history.pushState({ page }, '', url);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const onPop = (e) => {
      const page = e.state?.page || getPageFromHash();
      setActive(page);
      setScrollActive(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('popstate', onPop);
    const initialUrl = active === 'Home' ? '/' : `#${active}`;
    window.history.replaceState({ page: active }, '', initialUrl);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (active !== 'Home') return;
    const sections = [
      { id: 'section-home', nav: 'Home' },
      { id: 'section-features', nav: 'Features' },
      { id: 'section-about', nav: 'About' },
      { id: 'section-pricing', nav: 'Pricing' },
      { id: 'section-contact', nav: 'Contact' },
    ];
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const found = sections.find(s => s.id === entry.target.id);
          if (found) setScrollActive(found.nav);
        }
      });
    }, { threshold: 0.05, rootMargin: '-68px 0px -55% 0px' });
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [active]);

  const renderPage = () => {
    switch(active) {
      case 'Home': return (
        <>
          <div id="section-home"><Hero setActive={handleSetActive} /></div>
          <ProblemWeSolve />
          <div id="section-features"><Features setActive={handleSetActive} /></div>
          <div id="section-about"><OurStory /></div>
          <HowItWorks setActive={handleSetActive} />
          <Testimonials />
          <div id="section-pricing"><Pricing setActive={handleSetActive} /></div>
          <DataOwnership setActive={handleSetActive} />
          <FAQ />
          <div id="section-contact"><Contact /></div>
        </>
      );
      case 'About': return <><About /><OurStory /><DataOwnership setActive={handleSetActive} /></>;
      case 'Features': return <Features setActive={handleSetActive} />;
      case 'Pricing': return <Pricing setActive={handleSetActive} />;
      case 'Contact': return <Contact />;
      default: return <Hero setActive={handleSetActive} />;
    }
  };

  const navActive = active === 'Home' ? scrollActive : active;

  return (
    <div className="app">
      <Navbar active={navActive} setActive={handleSetActive} />
      <main>{renderPage()}</main>
      <Footer setActive={handleSetActive} />
      <WhatsAppButton />
    </div>
  );
}
